import { ReactNode, useContext, useEffect, useState } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { Box, Grid, Theme, Tooltip, useTheme } from '@mui/material';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { MetadataError } from '@ep/error-handling';
import LoadingScreen from './loading/LoadingScreen';
import GhostButton from './button/GhostButton';
import { Heading } from './typography/PrimaryTypography';
import { ReactComponent as ConfigurationIcon } from '../../assets/icons/Configuration-09.svg';
import AccessDenied from '../../views/shared/AccessDenied';
import { UserContext } from '../../context/UserContext';
import { GroupContext } from '../../context/GroupContext';
import { CompanyContext } from '../../context/CompanyContext';
import { ViewAccess, getViewAccess } from '../../util/user_access';
import { generateUuidV4 } from '../../util/uuid';
import { log } from '../../util/log';
import { FeedbackSnackbarContext } from '../../context/FeedbackSnackbarContext';
import { FUNCTION_UNKNOWN_FAILURE } from '../../util/global_variables';

interface ConfigNavButton {
  route: string;
  label: string;
  icon: ReactNode;
}

const ConfigurationWrapper = ({
  title,
  icon,
  children,
  previous,
  next
}: {
  title: string;
  icon: ReactNode;
  children: ReactNode;
  previous: ConfigNavButton | null;
  next: ConfigNavButton | null;
}) => {
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const { user } = useContext(UserContext);
  const { group } = useContext(GroupContext);
  const { company } = useContext(CompanyContext);
  const [access, setAccess] = useState<ViewAccess>(null);
  const navigate: NavigateFunction = useNavigate();
  const theme: Theme = useTheme();

  const handleNavigate = (config_url: string): void => {
    navigate(config_url);
  };

  useEffect(() => {
    try {
      setAccess(
        group && company && user ? getViewAccess(user, group.id, company.id, 'configuration') : null
      );
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE('ConfigurationWrapper'),
          {
            user: user,
            group: group,
            company: company
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Could not verify user access. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
  }, [user, company]);

  return !user || access === null ? (
    <LoadingScreen />
  ) : access !== 'none' ? (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={4}>
        <Grid size={12}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 4,
              gap: 4,
              color: theme.palette.primary.main
            }}
          >
            {icon}
            <Heading weight="Medium">{title}</Heading>
          </Box>
          <Box
            sx={{
              textAlign: 'right',
              mb: 2
            }}
          ></Box>
          <Box sx={{ minHeight: '60vh', width: '100%' }}>{children}</Box>
        </Grid>
      </Grid>
      <Box sx={{ display: 'flex', my: 2 }}>
        <GhostButton
          startIcon={<KeyboardArrowLeftIcon />}
          endIcon={
            <ConfigurationIcon
              height={30}
              fill={theme.palette.teal.dark}
              stroke={theme.palette.teal.dark}
            />
          }
          dark
          outlined
          onClick={() => handleNavigate('/configuration')}
          sx={{ width: '100px', height: '50px', mr: 2 }}
        />
        {previous && (
          <Tooltip title={`Navigate to ${previous.label}`}>
            <GhostButton
              startIcon={<KeyboardArrowLeftIcon />}
              endIcon={previous.icon}
              dark
              outlined
              onClick={() => handleNavigate(previous.route)}
              sx={{ minWidth: '250px', height: '50px', mr: 'auto' }}
            >
              {previous.label}
            </GhostButton>
          </Tooltip>
        )}
        {next && (
          <Tooltip title={`Navigate to ${next.label}`}>
            <GhostButton
              startIcon={next.icon}
              endIcon={<KeyboardArrowRightIcon />}
              dark
              outlined
              onClick={() => handleNavigate(next.route)}
              sx={{ minWidth: '250px', height: '50px', ml: 'auto' }}
            >
              {next.label}
            </GhostButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  ) : (
    <AccessDenied />
  );
};

export default ConfigurationWrapper;
